<template>
  <div class="organization-page">
    <!-- Page header: title, subtitle and primary action -->
    <div class="org-page-header">
      <div class="org-page-header__text">
        <h2 class="org-page-title">
          <Icon
            type="md-git-network"
            class="org-page-title__icon"
          />
          {{ $t("Organization") }}
        </h2>
        <p class="org-page-subtitle">
          {{ $t("Structure your organization into entities (subsidiaries, business units, teams) and group countries into zones.") }}
        </p>
      </div>

      <!-- Client picker: operators/superusers act across clients, so they must
           pick which client's tree they are working on. -->
      <div
        v-if="showClientPicker"
        class="org-client-picker"
      >
        <span class="org-client-picker__label">{{ $t("Client") }}</span>
        <Select
          v-model="selectedClientId"
          filterable
          :placeholder="$t('Select a client')"
          style="width: 240px"
          @on-change="onClientChange"
        >
          <Option
            v-for="c in clients"
            :key="c.id"
            :value="c.id"
          >
            {{ c.client_name }}
          </Option>
        </Select>
      </div>
    </div>

    <Tabs
      v-model="activeTab"
      class="org-tabs"
      @on-click="onTabChange"
    >
      <TabPane
        :label="$t('Organization tree')"
        name="tree"
        icon="md-git-network"
      >
        <div class="org-card">
          <!-- Toolbar: entity count + add-root action -->
          <div class="org-toolbar">
            <div class="org-toolbar__info">
              <span class="org-count-badge">{{ entityCount }}</span>
              <span class="org-count-label">
                {{ entityCount > 1 ? $t("entities") : $t("entity") }}
              </span>
            </div>
            <Button
              v-if="canEdit"
              type="primary"
              icon="md-add"
              @click="openCreate(null)"
            >
              {{ $t("Add root entity") }}
            </Button>
          </div>

          <Spin
            v-if="organizationStore.loading"
            fix
          />

          <div
            v-if="organizationStore.tree.length"
            class="org-tree"
          >
            <OrgTreeNode
              v-for="root in organizationStore.tree"
              :key="root.id"
              :node="root"
              :depth="0"
              :can-edit="canEdit"
              @add="openCreate($event.id)"
              @edit="openEdit"
              @move="openMove"
              @attach="openAttach"
              @remove="confirmDelete"
            />
          </div>
          <div
            v-else-if="!organizationStore.loading"
            class="org-empty"
          >
            <Icon
              type="md-git-network"
              class="org-empty__icon"
            />
            <p class="org-empty__title">
              {{ $t("No entity yet") }}
            </p>
            <p class="org-empty__text">
              {{ $t("No entity yet. Create a root entity to start your organization tree.") }}
            </p>
            <Button
              v-if="canEdit"
              type="primary"
              icon="md-add"
              @click="openCreate(null)"
            >
              {{ $t("Add root entity") }}
            </Button>
          </div>
        </div>
      </TabPane>

      <TabPane
        :label="$t('Zones')"
        name="zones"
        icon="md-globe"
      >
        <div class="org-card">
          <ZonesTab :can-edit="canEdit" />
        </div>
      </TabPane>
    </Tabs>

    <!-- Create / edit modal -->
    <Modal
      v-model="formModal.show"
      :title="formModal.id ? $t('Edit entity') : $t('New entity')"
      @on-ok="submitForm"
    >
      <Form :label-width="110">
        <FormItem :label="$t('Name')">
          <Input
            v-model="formModal.name"
            :placeholder="$t('Entity name')"
          />
        </FormItem>
        <FormItem :label="$t('Type')">
          <Input
            v-model="formModal.entity_type"
            :placeholder="$t('e.g. subsidiary, BU, team')"
          />
        </FormItem>
      </Form>
    </Modal>

    <!-- Move modal -->
    <Modal
      v-model="moveModal.show"
      :title="$t('Move entity')"
      @on-ok="submitMove"
    >
      <Form :label-width="110">
        <FormItem :label="$t('New parent')">
          <Select
            v-model="moveModal.parentId"
            clearable
            :placeholder="$t('Root (no parent)')"
          >
            <Option
              v-for="opt in moveParentOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </Option>
          </Select>
        </FormItem>
      </Form>
    </Modal>

    <!-- Attach user modal -->
    <Modal
      v-model="attachModal.show"
      :title="$t('Attach a user')"
      @on-ok="submitAttach"
    >
      <Form :label-width="110">
        <FormItem :label="$t('User')">
          <Select
            :key="attachSelectKey"
            ref="attachUserSelect"
            v-model="attachModal.userId"
            filterable
            :loading="usersLoading"
            :placeholder="$t('Select a user')"
            :not-found-text="usersLoading ? $t('Loading…') : $t('No user in this client')"
            @on-change="clearUserSearchQuery"
          >
            <Option
              v-for="u in users"
              :key="u.id"
              :value="u.id"
            >
              {{ u.full_name || u.email }}
            </Option>
          </Select>
        </FormItem>
      </Form>
    </Modal>
  </div>
</template>

<script>
import { getUsers, getClientsQry } from "@/services/accounts.service";
import ZonesTab from "./ZonesTab.vue";
import OrgTreeNode from "./OrgTreeNode.vue";

// Remember the operator's last-selected client across page refreshes.
const CLIENT_STORAGE_KEY = "org_selected_client_id";

export default {
  name: "Organization",
  components: { ZonesTab, OrgTreeNode },
  data() {
    return {
      // Active tab, restored from the URL hash (#tree / #zones) so a refresh
      // keeps the user on the tab they were viewing.
      activeTab: this.tabFromHash(),
      users: [],
      usersLoading: false,
      clients: [],
      selectedClientId: null,
      formModal: { show: false, id: null, name: "", entity_type: "", parentId: null },
      moveModal: { show: false, id: null, parentId: null },
      attachModal: { show: false, entityId: null, userId: null },
      // Bumped each time the attach modal opens, to force-remount the user <Select>
      // so iView clears its sticky internal search query between openings.
      attachSelectKey: 0,
    };
  },
  computed: {
    canEdit() {
      // Managers and above may edit the tree; others get a read-only view.
      return this.isManagerOrAbove();
    },
    showClientPicker() {
      // Only cross-client roles (operator/superuser) need to choose a client;
      // a manager is bound to their own single client.
      return this.isOperatorOrAbove();
    },
    entityCount() {
      // Total number of entities across the whole tree.
      const count = (nodes) =>
        (nodes || []).reduce((acc, n) => acc + 1 + count(n.children), 0);
      return count(this.organizationStore.tree);
    },
    moveParentOptions() {
      // Any entity except the one being moved (server rejects cycles anyway).
      return this.organizationStore.entityOptions.filter(
        (o) => o.value !== this.moveModal.id
      );
    },
  },
  created() {
    if (this.showClientPicker) {
      // Operators pick a client; load the list and restore the last-used one
      // (persisted in localStorage) so a page refresh keeps the same context.
      getClientsQry().then((res) => {
        this.clients = Array.isArray(res.data) ? res.data : res.data.results || [];
        if (this.clients.length) {
          const saved = Number(localStorage.getItem(CLIENT_STORAGE_KEY));
          const stillExists = this.clients.some((c) => c.id === saved);
          this.selectedClientId = stillExists ? saved : this.clients[0].id;
          this.reload();
        }
      });
    } else {
      // A manager works on their own client; no picker needed.
      this.reload();
    }
    // Users are loaded per entity (filtered by that entity's client) when the
    // "attach user" modal opens — see openAttach().
  },
  mounted() {
    // Normalise the hash to the resolved tab (e.g. "#Zones" -> "#zones").
    this.onTabChange(this.activeTab);
  },
  methods: {
    /* ---- Tabs (persist active tab in the URL hash) ---- */
    tabFromHash() {
      // Accept #zones / #Zones / #tree (case-insensitive); default to "tree".
      const raw = (window.location.hash || "").replace(/^#/, "").toLowerCase();
      return raw === "zones" ? "zones" : "tree";
    },
    onTabChange(name) {
      // Reflect the active tab in the URL without adding a history entry.
      const hash = "#" + name;
      if (window.location.hash !== hash) {
        history.replaceState(null, "", window.location.pathname + window.location.search + hash);
      }
    },

    /* ---- Create / edit ---- */
    openCreate(parentId) {
      this.formModal = { show: true, id: null, name: "", entity_type: "", parentId };
    },
    openEdit(entity) {
      this.formModal = {
        show: true,
        id: entity.id,
        name: entity.name,
        entity_type: entity.entity_type || "",
        parentId: entity.parent,
      };
    },
    async submitForm() {
      if (!this.formModal.name) {
        this.$Message.warning(this.$t("Name is required"));
        return;
      }
      try {
        if (this.formModal.id) {
          await this.organizationStore.updateEntity(this.formModal.id, {
            name: this.formModal.name,
            entity_type: this.formModal.entity_type,
          });
        } else {
          const payload = {
            name: this.formModal.name,
            entity_type: this.formModal.entity_type,
            parent: this.formModal.parentId,
          };
          // A child inherits its client from the parent (server-side); only a
          // root needs an explicit client.
          if (!this.formModal.parentId) {
            payload.client = this.currentClientId();
          }
          await this.organizationStore.createEntity(payload);
        }
        this.$Message.success(this.$t("Saved"));
      } catch (err) {
        this.handleApiError(err);
      }
    },

    /* ---- Move ---- */
    openMove(entity) {
      this.moveModal = { show: true, id: entity.id, parentId: entity.parent };
    },
    async submitMove() {
      try {
        await this.organizationStore.moveEntity(this.moveModal.id, this.moveModal.parentId || null);
        this.$Message.success(this.$t("Entity moved"));
      } catch (err) {
        this.handleApiError(err);
      }
    },

    /* ---- Attach user ---- */
    openAttach(entity) {
      this.attachModal = { show: true, entityId: entity.id, userId: null };
      // Force the user <Select> to re-mount so its internal search query is reset
      // (iView keeps the previous query otherwise → "sticky filter" bug).
      this.attachSelectKey += 1;
      // Only users of THIS entity's client can be attached (tenant isolation):
      // reload the list filtered by the entity's client each time.
      this.loadAttachableUsers(entity.client);
    },
    clearUserSearchQuery() {
      // After picking a user, clear the Select's internal search query so the next
      // search starts from the full list (iView keeps the query otherwise).
      this.$nextTick(() => {
        const sel = this.$refs.attachUserSelect;
        if (sel) {
          sel.query = "";
          if (sel.$refs && sel.$refs.input) sel.$refs.input.currentValue = "";
        }
      });
    },
    async loadAttachableUsers(clientId) {
      this.users = [];
      this.usersLoading = true;
      try {
        const params = clientId ? { client: clientId } : {};
        const res = await getUsers(params);
        this.users = Array.isArray(res.data) ? res.data : res.data.results || [];
      } catch (err) {
        this.handleApiError(err);
      } finally {
        this.usersLoading = false;
      }
    },
    async submitAttach() {
      if (!this.attachModal.userId) {
        this.$Message.warning(this.$t("Select a user"));
        return;
      }
      try {
        await this.organizationStore.attachUser(this.attachModal.entityId, this.attachModal.userId);
        this.$Message.success(this.$t("User attached"));
      } catch (err) {
        this.handleApiError(err);
      }
    },

    /* ---- Delete ---- */
    confirmDelete(entity) {
      this.$Modal.confirm({
        title: this.$t("Delete entity"),
        content: this.$t("This will also delete all its child entities. Continue?"),
        onOk: async () => {
          try {
            await this.organizationStore.removeEntity(entity.id);
            this.$Message.success(this.$t("Entity deleted"));
          } catch (err) {
            this.handleApiError(err);
          }
        },
      });
    },

    /* ---- Client picker / reload ---- */
    reload() {
      // Reload the tree (and zones tab data) for the currently scoped client.
      // selectedClientId is null for a manager -> backend uses their own client.
      this.organizationStore.fetchEntities(this.selectedClientId);
      this.organizationStore.fetchZones(this.selectedClientId);
    },
    onClientChange() {
      // Persist so a refresh restores the same client (see created()).
      if (this.selectedClientId) {
        localStorage.setItem(CLIENT_STORAGE_KEY, this.selectedClientId);
      }
      this.reload();
    },

    /* ---- Helpers ---- */
    currentClientId() {
      // For operators, the client chosen in the picker; otherwise the
      // authenticated user's own client (from session).
      if (this.selectedClientId) return this.selectedClientId;
      const authData = JSON.parse(localStorage.getItem("auth_data") || "{}");
      return (authData.client && authData.client.id) || authData.client || null;
    },
    handleApiError(err) {
      const data = err && err.response && err.response.data;
      let msg = this.$t("Something went wrong");
      if (data) {
        if (data.parent) msg = Array.isArray(data.parent) ? data.parent[0] : data.parent;
        else if (data.detail) msg = data.detail;
      }
      this.$Message.error(msg);
    },
  },
};
</script>

<style scoped>
.organization-page {
  padding: 24px;
  position: relative;
  background: #f5f7fa;
  min-height: 100%;
}

/* ---- Page header ---- */
.org-page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
.org-client-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.org-client-picker__label {
  color: #808695;
  font-size: 13px;
  font-weight: 500;
}
.org-page-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
  margin: 0;
}
.org-page-title__icon {
  font-size: 24px;
  color: #2d8cf0;
  margin-right: 10px;
}
.org-page-subtitle {
  margin: 6px 0 0 34px;
  color: #808695;
  font-size: 13px;
  max-width: 640px;
}

/* ---- Cards ---- */
.org-card {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  position: relative;
}
.org-tabs >>> .ivu-tabs-bar {
  margin-bottom: 16px;
}

/* ---- Toolbar ---- */
.org-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f2f5;
}
.org-toolbar__info {
  display: flex;
  align-items: center;
  gap: 8px;
}
.org-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 8px;
  border-radius: 13px;
  background: #e8f4ff;
  color: #2d8cf0;
  font-weight: 600;
  font-size: 13px;
}
.org-count-label {
  color: #808695;
  font-size: 13px;
}

/* ---- Tree container (node styling lives in OrgTreeNode.vue) ---- */
.org-tree {
  padding: 4px 2px;
}

/* Hint under the attach-user select */
.attach-hint {
  margin: 8px 0 0;
  color: #808695;
  font-size: 12px;
}

/* ---- Empty state ---- */
.org-empty {
  color: #808695;
  padding: 48px 20px;
  text-align: center;
}
.org-empty__icon {
  font-size: 56px;
  color: #dcdee2;
  margin-bottom: 12px;
}
.org-empty__title {
  font-size: 16px;
  font-weight: 600;
  color: #515a6e;
  margin: 0 0 4px;
}
.org-empty__text {
  margin: 0 0 20px;
  font-size: 13px;
}
</style>
